import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {FiChevronDown, FiChevronRight} from 'react-icons/fi'
import {
  FaBell,
  FaBookReader,
  FaBroadcastTower,
  FaBusinessTime,
  FaClipboardList,
  FaClock,
  FaCode,
  FaComments,
  FaEnvelope,
  FaEnvelopeOpenText,
  FaFacebookMessenger,
  FaFileAlt,
  FaFileCode,
  FaFileExport,
  FaFileInvoice,
  FaGlobe,
  FaGrinHearts,
  FaInstagram,
  FaList,
  FaListAlt,
  FaListUl,
  FaNetworkWired,
  FaPhoneAlt,
  FaRegChartBar,
  FaSms,
  FaTags,
  FaTasks,
  FaTwitter,
  FaUnlockAlt,
  FaUserCheck,
  FaUserCog,
  FaUserEdit,
  FaUserLock,
  FaUsers,
  FaUsersCog,
  FaWhatsapp,
} from 'react-icons/fa'
import {VmoLabel, VmoMenu, VmoMenuItem, VmoAccordion, VmoAccordionContent, VmoAccordionTitle} from 'vmo-library'
import PermissionIcon from 'components/common/PermissionIcon'
import useUserPermissions from 'hooks/pure/useUserPermission'
import {isActiveNavlink} from 'utils/helper'
import {SETTINGS_PATH} from 'constants/path'

const SettingsMenu = () => {
  const navigate = useNavigate()

  const [expand, setExpand] = useState([])

  const {
    advanceFeaturesAccess,
    conversationAccess,
    customerManagementAccess,
    omniChannelsAccess,
    workflowAutomationAccess,
    productivityPermissions,
  } = useUserPermissions()

  const menus = [
    {
      key: 'my-settings',
      title: 'My Settings',
      items: [
        {
          key: 'my-profile',
          path: SETTINGS_PATH.MY_PROFILE,
          icon: <FaUserEdit />,
          title: 'My Profile',
        },
      ],
    },
    {
      key: 'my-organization',
      title: 'My Organization',
      items: [
        {key: 'account-settings', path: SETTINGS_PATH.ACCOUNT_SETTINGS, icon: <FaUserCog />, title: 'Account Settings'},
        {key: 'users', path: SETTINGS_PATH.USERS, icon: <FaUsers />, title: 'Users'},
        {key: 'teams', path: SETTINGS_PATH.TEAMS, icon: <FaUsersCog />, title: 'Teams'},
        {key: 'roles', path: SETTINGS_PATH.ROLES, icon: <FaUserCheck />, title: 'Roles'},
        {key: 'notification', path: SETTINGS_PATH.NOTIFICATIONS, icon: <FaBell />, title: 'Notifications'},
      ],
    },
    {
      key: 'channels',
      title: 'Channels',
      items: [
        {key: 'emails', path: SETTINGS_PATH.EMAIL, icon: <FaEnvelope />, title: 'Email'},
        {
          key: 'widget',
          path: SETTINGS_PATH.SUPPORT_WIDGET,
          icon: <FaComments />,
          title: 'Support Widget',
          isFeatureAccessDenied: !omniChannelsAccess?.supportWidget,
        },
        {key: 'facebook', path: SETTINGS_PATH.FACEBOOK, icon: <FaFacebookMessenger />, title: 'Facebook Messenger'},
        {key: 'instagram', path: SETTINGS_PATH.INSTAGRAM, icon: <FaInstagram />, title: 'Instagram'},
        {key: 'whatsapp', path: SETTINGS_PATH.WHATSAPP, icon: <FaWhatsapp />, title: 'Whatsapp'},
        {key: 'contact-form', path: SETTINGS_PATH.CONTACT_FORM, icon: <FaFileAlt />, title: 'Contact Forms'},
        {key: 'chat-inbox', path: SETTINGS_PATH.LIVE_CHAT, icon: <FaComments />, title: 'Live Chat'},
        {key: 'phone', path: SETTINGS_PATH.PHONE, icon: <FaPhoneAlt />, title: 'Phone', newLabel: true},
        {key: 'sms', path: SETTINGS_PATH.SMS, icon: <FaSms />, title: 'SMS'},
      ],
    },
    {
      key: 'custom-fields',
      title: 'Custom Fields',
      items: [
        {
          key: 'conversations',
          path: SETTINGS_PATH.CONVERSATIONS,
          icon: <FaEnvelopeOpenText />,
          title: 'Conversations',
          newLabel: true,
        },
        {
          key: 'customer',
          exact: true,
          path: SETTINGS_PATH.CUSTOMERS,
          icon: <FaUsers />,
          title: 'Customers',
          isFeatureAccessDenied: !customerManagementAccess?.customFields,
        },
      ],
    },
    {
      key: 'automations',
      title: 'Automations',
      items: [
        {
          key: 'routing',
          title: 'Routing',
          icon: <FaBroadcastTower />,
          path: SETTINGS_PATH.ROUTING,
        },
        {
          key: 'workflows',
          path: SETTINGS_PATH.WORKFLOWS,
          icon: <FaClipboardList />,
          title: 'Workflows',
          isFeatureAccessDenied: !workflowAutomationAccess?.advanceWorkflows,
        },
        {
          title: 'Business Hours',
          icon: <FaBusinessTime />,
          path: SETTINGS_PATH.BUSINESS_HOURS,
          key: 'business-hours',
        },
        {
          key: 'sla-management',
          path: SETTINGS_PATH.SLA_MANAGEMENT,
          icon: <FaBookReader />,
          title: 'SLA Management',
          isFeatureAccessDenied: !workflowAutomationAccess?.slaManagement,
        },
        {
          key: 'time-tracking',
          title: 'Time Tracking',
          icon: <FaClock />,
          path: SETTINGS_PATH.TIME_TRACKING,
          isFeatureAccessDenied: !conversationAccess?.timeTracking,
        },
      ],
    },
    {
      key: 'productivity',
      title: 'Productivity',
      items: [
        {
          key: 'tags',
          title: 'Tags',
          icon: <FaTags />,
          path: SETTINGS_PATH.TAGS,
        },
        {
          key: 'canned-responses',
          title: 'Email Templates',
          icon: <FaClipboardList />,
          path: SETTINGS_PATH.EMAIL_TEMPLATES,
        },
        {
          key: 'customer-satisfaction',
          title: 'Customer Satisfaction',
          icon: <FaGrinHearts />,
          path: SETTINGS_PATH.CUSTOMER_SATISFACTION,
          isFeatureAccessDenied: !advanceFeaturesAccess?.csatSurveys,
        },
        {
          key: 'canned-forms',
          title: 'Canned Forms',
          icon: <FaListAlt />,
          path: SETTINGS_PATH.CANNED_FORMS,
          newLabel: true,
        },
        {
          key: 'audit-logs',
          title: 'Audit Logs',
          path: SETTINGS_PATH.AUDIT_LOGS,
          icon: <FaListUl />,
          isFeatureAccessDenied: !workflowAutomationAccess.auditLogs,
          isRoleAccessDenied: !productivityPermissions?.audit_logs,
        },
      ],
    },
    {
      key: 'task',
      title: 'Tasks',
      items: [
        {
          key: 'templates',
          title: 'Task Templates',
          icon: <FaList />,
          path: SETTINGS_PATH.TASK_TEMPLATES,
          isFeatureAccessDenied: !advanceFeaturesAccess?.taskTemplatesAndAtomation,
        },
        {
          key: 'task-automation',
          title: 'Task Automation',
          icon: <FaTasks />,
          path: SETTINGS_PATH.TASK_AUTOMATION,
          isFeatureAccessDenied: !advanceFeaturesAccess?.taskTemplatesAndAtomation,
        },
      ],
    },

    {
      key: 'reports',
      title: 'Reports',
      items: [
        {
          key: 'schedule-reports',
          title: 'Schedule reports',
          icon: <FaRegChartBar />,
          path: SETTINGS_PATH.SCHEDULE_REPORTS,
        },
        {
          key: 'export',
          title: 'Export',
          icon: <FaFileExport />,
          newLabel: true,
          path: SETTINGS_PATH.EXPORT,
        },
      ],
    },
    {
      key: 'billing',
      title: 'Billing',
      items: [
        {
          key: 'my-plan',
          title: 'My Plan',
          icon: <FaUserLock />,
          path: SETTINGS_PATH.MY_PLAN,
        },
        {
          key: 'invoices',
          title: 'Invoices',
          icon: <FaFileInvoice />,
          path: SETTINGS_PATH.INVOICES,
        },
      ],
    },
    {
      key: 'apps',
      title: 'Apps',
      items: [
        {
          key: 'developer-api',
          title: 'Developer API',
          icon: <FaCode />,
          path: SETTINGS_PATH.DEVELOPER_API,
        },
        {
          key: 'integrations',
          title: 'Integrations',
          icon: <FaFileCode />,
          path: SETTINGS_PATH.INTEGRATIONS,
        },
        {
          key: 'security',
          title: 'Security',
          icon: <FaUnlockAlt />,
          newLabel: true,
          path: SETTINGS_PATH.SECURITY,
        },
        {
          key: 'custom-domain',
          title: 'Custom Domain',
          icon: <FaGlobe />,
          path: SETTINGS_PATH.CUSTOM_DOMAIN,
          newLabel: true,
        },
      ],
    },
    {
      key: 'help',
      title: 'Help',
      items: [
        {
          key: 'keyboard-shortcuts',
          title: 'Keyboard Shortcuts',
          icon: <FaCode />,
          path: SETTINGS_PATH.KEYBOARD_SHORTCUTS,
        },
      ],
    },
  ]
  const handleMenuExpand = key => {
    const menus = [...expand]
    if (menus.includes(key)) {
      menus.splice(menus.indexOf(key), 1)
      setExpand(menus)
    } else {
      setExpand([...menus, key])
    }
  }
  const isActive = key => !expand.includes(key)
  return (
    <VmoMenu vertical secondary className="set-list">
      {menus.map(menu => (
        <VmoAccordion key={menu.key}>
          <VmoAccordionTitle onClick={() => handleMenuExpand(menu.key)}>
            {menu.title}
            {isActive(menu.key) ? <FiChevronDown /> : <FiChevronRight />}
          </VmoAccordionTitle>
          <VmoAccordionContent active={isActive(menu.key)}>
            {menu.items.map(route => (
              <VmoMenuItem
                key={route.key}
                active={isActiveNavlink(`${route.path}`, route.exact)}
                onClick={() => navigate(`${route.path}`)}
              >
                {route?.icon}
                {route?.title}
                <span>
                  {' '}
                  <PermissionIcon
                    isFeatureAccessDenied={route?.isFeatureAccessDenied}
                    isRoleAccessDenied={route?.isRoleAccessDenied}
                  />
                </span>

                {route?.newLabel && <VmoLabel>New</VmoLabel>}
              </VmoMenuItem>
            ))}
          </VmoAccordionContent>
        </VmoAccordion>
      ))}
    </VmoMenu>
  )
}

export default SettingsMenu
